import { useQuery } from '@tanstack/react-query';
import type { ZodType } from 'zod';
import {
  bestPerRegionListSchema,
  countryGapListSchema,
  countryOpportunityListSchema,
  countryListSchema,
  flopListSchema,
  imageStatListSchema,
  potentialListSchema,
  rateListSchema,
  retryListSchema,
  regionListSchema,
} from '@competa/shared';
import { apiRequest } from '../../api/client';

/**
 * Lädt eine Auswertung des angemeldeten Benutzers.
 * @param path Pfad unterhalb von `/analytics`, ggf. mit Abfrageparametern.
 * @param schema Schema der Antwort.
 * @returns Das Abfrageergebnis.
 */
export function useAnalytics<T>(path: string, schema: ZodType<T>) {
  return useQuery({
    queryKey: ['analytics', path],
    queryFn: () => apiRequest('GET', `/analytics${path}`, { schema }),
  });
}

/** Themen der Detailseiten: Pfad, Schema und Übersetzungsschlüssel je Auswertung. */
export const TOPICS = {
  'top-images': { path: '/top-images?limit=200', schema: imageStatListSchema },
  overlimit: { path: '/overlimit', schema: imageStatListSchema },
  potential: { path: '/potential?limit=200', schema: potentialListSchema },
  flops: { path: '/flops?limit=200', schema: flopListSchema },
  categories: { path: '/categories', schema: rateListSchema },
  countries: { path: '/countries', schema: countryListSchema },
  regions: { path: '/regions', schema: regionListSchema },
  'best-per-region': { path: '/best-per-region', schema: bestPerRegionListSchema },
  'country-gaps': { path: '/country-gaps', schema: countryGapListSchema },
  'retry-suggestions': {
    path: '/retry-suggestions?perImage=all&limit=200',
    schema: retryListSchema,
  },
  'country-opportunities': {
    path: '/country-opportunities?limit=200',
    schema: countryOpportunityListSchema,
  },
} as const;

/** Name eines Themas. */
export type Topic = keyof typeof TOPICS;
